import { ConfirmOptions } from "./ConfirmProvider";

/**
 * Ready-made confirmation copy for the common destructive actions, matching the
 * StepUp confirmation spec. Spread a preset into `confirm()` and add
 * `onConfirm` / `successMessage`:
 *
 *   confirm({ ...confirmPresets.deletePermanent("student"), onConfirm: () => del(id).unwrap(), successMessage: "Deleted" })
 */

/** Permanent, irreversible delete. */
export const deletePermanent = (thing = "record"): ConfirmOptions => ({
  variant: "danger",
  title: "Delete Record",
  message: `This action cannot be undone.\nAre you sure you want to delete this ${thing}?`,
  confirmText: "Delete Permanently",
  cancelText: "Cancel",
});

/** Generic delete/remove that is recoverable or soft. */
export const remove = (thing = "item"): ConfirmOptions => ({
  variant: "danger",
  title: "Confirm Action",
  message: `Are you sure you want to remove this ${thing}?`,
  confirmText: "Yes, Proceed",
  cancelText: "Cancel",
});

/** Unlink a relationship without deleting either record. */
export const unlink = (message: string): ConfirmOptions => ({
  variant: "warning",
  title: "Confirm Action",
  message,
  confirmText: "Yes, Proceed",
  cancelText: "Cancel",
});

/** Unlink a parent from a child (keeps both records). */
export const unlinkParentChild = (): ConfirmOptions =>
  unlink("Are you sure you want to remove this parent's access to this child?\nThe child record will not be deleted.");

/** Disable a login account. */
export const disableAccount = (): ConfirmOptions => ({
  variant: "warning",
  title: "Confirm Action",
  message: "Are you sure you want to disable this account?\nThe user will no longer be able to log in.",
  confirmText: "Yes, Disable",
  cancelText: "Cancel",
});

/** Disable access to a single child. */
export const disableAccess = (message = "Are you sure you want to disable this access?"): ConfirmOptions => ({
  variant: "warning",
  title: "Confirm Action",
  message,
  confirmText: "Yes, Disable",
  cancelText: "Cancel",
});

/** Reset / regenerate a password. */
export const resetPassword = (message = "Are you sure you want to reset this password?"): ConfirmOptions => ({
  variant: "warning",
  title: "Confirm Action",
  message,
  confirmText: "Yes, Proceed",
  cancelText: "Cancel",
});

/** A generic proceed prompt. */
export const proceed = (message = "Are you sure you want to proceed?"): ConfirmOptions => ({
  variant: "default",
  title: "Confirm Action",
  message,
  confirmText: "Yes, Proceed",
  cancelText: "Cancel",
});
